/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { untranslated, useTranslation } from "@i18n";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BiGitCompare } from "react-icons/bi";
import { GiHamburgerMenu } from "react-icons/gi";
import { MdArrowDropDown } from "react-icons/md";
import { RiErrorWarningFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { Box, IconButton } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import fileConversionStatusInProgress from "@Assets/icons/svg/FileConversionStatusInProgress.svg?react";
import fileConversionStatusUnsuccessfull from "@Assets/icons/svg/FileConversionStatusUnsuccessfull.svg?react";
import { convertDate } from "@Services/luxonHelper";
import DesignViewerLayoutManager from "@Features/design-viewer/components/DesignViewerLayoutManager";
import { setMinimizerData } from "@Reducers/minimizerFloatter";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getViewerUrl, extractRedirectUrl, extractRedirectUrlAsString } from "@Helpers";
import { isDocumentNumberField } from "@Helpers/constants";
import { setViewerState, setModalStatus, setShowOnlyModal } from "@Reducers/Viewer";
import { getFileImage } from "@Services/utils/iconMap";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import * as Sentry from "@Sentry";
import { BIM_ENGINE, CAD_ENGINE, PDF_ENGINE } from "@Services/constants";
import useModulesMapper from "@Components/StickySidebarViewer/ModulesMapper.jsx";
import _ from "lodash";
import { getTransmittalAttachedFiles } from "@Features/transmittals";
import { getChildrenFilesName } from "@Actions/Viewer";
import { selectFileAccessFor } from "@Reducers/AccessPermissionReducer";
import { setHelpSectionState } from "@Reducers/portalDashboard";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { RootState } from "@Reducers/index";
import type { SourceFileVersionData } from "@Types/fileOperations";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TooltipProps } from "@mui/material/Tooltip";
import type { TranslatedString } from "@i18n";
import type { ModalEntry } from "@Reducers/Viewer";
import type { HoopsViewerHandle } from "@Features/design-viewer/components/DesignViewerLayoutSetting";
import type { SidebarModuleEntry } from "@Features/design-viewer/components/DesignViewerLayoutManager";
import { getShareViewerSession, getShareViewerVersions } from "@Features/external-share/shareViewerSession";
import { browseSharedFolder } from "@Features/external-share/sharedLink.api";
import { objectIdString } from "@Helpers/schemas/common.schema";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";

/** One row of the file-version dropdown, as built by DesignViewer's `getFileVersionsDropDownItems`. */
export type FileVersionDropDownItem = {
  text: string;
  url: string;
  createdDate: number;
  versionNumber: number;
  awsFileName: string;
  fileVersionId: string;
};

/** The merged-file conversion banner state, as computed by DesignViewer. */
export type ConstituentFilesProcessingStatus = {
  status: string;
  count?: number;
  message?: string;
  buttonContent?: string;
};

/**
 * A row of the file-name dropdown. Two sources feed it — `getChildrenFilesName` (sibling files
 * under the same parent, which supply `fileVersion`) and `getTransmittalAttachedFiles` (files
 * shared through a transmittal, which supply `fileVersionId`/`toSpace`) — so each source's own
 * members are optional here.
 */
type HeaderFileListItem = Awaited<ReturnType<typeof getChildrenFilesName>>[number] & {
  fileVersionId?: string;
  toSpace?: string;
};

/**
 * `NamedNodeMap` is declared in lib.dom with only a numeric index signature, but the handlers below
 * read the `id` attribute by name off a live element.
 */
type AttributesByName = NamedNodeMap & { id: { value: string } };

interface DesignViewerHeaderProps {
  /** `null` until DesignViewer has resolved whether the route carries a transmittalId. */
  isPageOpenedThroughTransmittal: boolean | null;
  /** Genuinely a union: the transmittal effects seed it with "" before a row supplies the index. */
  currentVersionNumber: number | string;
  fileName: string;
  /** `undefined` when neither dropdown branch applies — the render already optional-chains it. */
  fileVersionsDropDownItems?: FileVersionDropDownItem[];
  handleComparedFileModelChange: (val: string[]) => void;
  handleComparedFileVersionChange: (val: string) => void;
  handleFileVersionIdChange: (val: string) => void;
  /** `null` until the viewer shell has handed its rc-dock handle up to DesignViewer. */
  HoopsViewer: HoopsViewerHandle | null;
  currentfileVersionId: string;
  engineType: string;
  modalToOpen: string[];
  enableDesignViewerLayoutManager?: boolean;
  estimateId?: string | null;
  scheduleId?: string | null;
  /** Forwarded by DesignViewer but not read here. */
  scheduleVersionId?: string | null;
  constituentFilesProcessingStatus: ConstituentFilesProcessingStatus | null;
  resetDesignViewer: () => void;
  setTransmittalId: (id: string) => void;
  /**
   * Recipient share view (CP-283). Suppresses the layout/tool controls (FR-19 permits mouse-driven
   * navigation only) and the app-shell window chrome, which a recipient has nothing to return to.
   * Absent in the authenticated app, so the header is unchanged there.
   */
  isShareView?: boolean;
}

function DesignViewerHeader({
  isPageOpenedThroughTransmittal,
  currentVersionNumber,
  fileName,
  fileVersionsDropDownItems,
  handleComparedFileModelChange,
  handleComparedFileVersionChange,
  handleFileVersionIdChange,
  HoopsViewer,
  currentfileVersionId,
  engineType,
  modalToOpen,
  enableDesignViewerLayoutManager,
  estimateId,
  scheduleId,
  constituentFilesProcessingStatus,
  resetDesignViewer,
  setTransmittalId,
  isShareView = false,
}: DesignViewerHeaderProps) {
  const { t } = useTranslation();
  const projectData = useSelector((state: RootState) => state.projectData);
  const helpSectionState = useSelector((state: RootState) => state.helpSectionState);
  // The single isFile403 result, fetched once by DesignViewer.
  const fileAccess = useSelector((state: RootState) => selectFileAccessFor(state, currentfileVersionId));
  // Select the primitive, not the entry. `state.filesData[id] || {}` allocated a fresh object every
  // time the selector ran, so useSelector's Object.is check never held and this component re-rendered
  // on every dispatched action. `parentId` is a string, compared by value.
  const parentId = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.parentId);
  const layout = useSelector((state: RootState) => state.layout);
  const showDocAndRevisionInViewer = !!layout?.FILE_MANAGER?.SHOW_DOC_AND_REVISION_IN_VIEWER;
  const customFieldValues = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.customFields);
  const fileFormTemplate = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.formTemplate);
  const revisionLabel = useSelector((state: RootState) => state.filesData[currentfileVersionId]?.revisionSeries?.label);
  const documentNumber = React.useMemo(() => {
    if (!showDocAndRevisionInViewer || !customFieldValues) return "";
    const sections = (fileFormTemplate ?? []) as { questionFields?: { _id?: string; questionValue?: string }[] }[];
    for (const section of sections) {
      const question = (section?.questionFields ?? []).find((field) => isDocumentNumberField(field?.questionValue));
      if (question?._id) return String(customFieldValues[question._id] ?? "");
    }
    return "";
  }, [showDocAndRevisionInViewer, customFieldValues, fileFormTemplate]);
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const fileVersions = useSelector((state: RootState) => state.fileVersions);

  const navigate = useNavigate();
  const MAXIMIZE = "maximize";
  const MINIMIZE = "minimize";
  const DISMISS = "dismiss";
  const ICON_DEFAULT = "ICON_DEFAULT";
  const ICON_ARROW_MAXIMIZE = "ICON_ARROW_MAXIMIZE";
  const ICON_ARROW_MINIMIZE = "ICON_ARROW_MINIMIZE";

  const [openVersionDropdown, setOpenVersionDropdown] = useState(false);
  // Static per mount: the viewer page repopulates the holder (and remounts this header, keyed by
  // file id) on every navigation, so reading once at mount is always current.
  const shareVersions = isShareView ? getShareViewerVersions() : [];
  // QA r4 #2: the share top-bar dropdowns are the STANDARD MenuComponent, anchor-driven.
  const [shareBurgerAnchor, setShareBurgerAnchor] = useState<HTMLElement | null>(null);
  const [shareFilesAnchor, setShareFilesAnchor] = useState<HTMLElement | null>(null);
  const [shareVersionAnchor, setShareVersionAnchor] = useState<HTMLElement | null>(null);
  /**
   * Sibling files of the open one, scoped to the folder the recipient was browsing (the ?folder=
   * marker the shared browser put on this route — QA r4 #2: folder A's file lists A's siblings,
   * never folder B's). A file-only link has no marker and no siblings — the caret never renders.
   */
  const [shareSiblings, setShareSiblings] = useState<{ name: string; versionId: string; extension?: string }[]>([]);

  const [FileNameDropdown, setFileNameDropdown] = useState(false);
  const [inputHeading, setInputHeading] = useState("");
  const [showMinimizeIcon, setShowMinimizeIcon] = useState(false);
  const [showDismissIcon, setShowDismissIcon] = useState(false);
  const [childrenFiles, setChildrenFiles] = useState<HeaderFileListItem[]>([]);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    if (!isShareView) return undefined;
    const session = getShareViewerSession();
    const folderParam = new URLSearchParams(location.search).get("folder");
    if (!session || !folderParam) {
      setShareSiblings([]);
      return undefined;
    }
    const parsedFolder = folderParam === "root" ? null : objectIdString.safeParse(folderParam);
    if (parsedFolder && !parsedFolder.success) {
      setShareSiblings([]);
      return undefined;
    }
    let cancelled = false;
    // The bare share client — never axiosWrapper — so a guest cannot be 401-bounced to sign-in.
    browseSharedFolder({
      token: session.token,
      sessionToken: session.sessionToken,
      ...(parsedFolder?.success ? { folderId: parsedFolder.data } : {}),
      take: 200,
    })
      .then((res) => {
        if (cancelled || !res.status || !res.valid || res.requiresPassword) return;
        setShareSiblings(
          (res.entries ?? [])
            .filter((entry) => !entry.isFolder && entry.latestVersionId && (entry.isPreviewable || session.canDownload))
            .map((entry) => ({
              name: entry.name,
              versionId: String(entry.latestVersionId),
              extension: entry.extension,
            })),
        );
      })
      .catch((error: unknown) => {
        // Decorative list: a failed read leaves the dropdown empty rather than surfacing an error,
        // but the failure is still reported — a broken browse here usually means a broken share.
        Sentry.captureException(error);
      });
    return () => {
      cancelled = true;
    };
  }, [isShareView, location.search]);

  /** Share navigation preserves the ?folder= return marker (QA r4 #1). */
  const navigateToSharedVersion = (versionId: string) => {
    const session = getShareViewerSession();
    if (session) navigate(`/share/${session.token}/file/${versionId}${location.search}`);
  };

  const Url = new URLSearchParams(location.search);
  const spaceId = Url.get("spaceId");
  const [maximizeIconState, setMaximizeIconState] = useState(ICON_DEFAULT);
  const [modulesMapper] = useModulesMapper({
    estimateId: !!Url.get("estimateId"),
    fileVersionId: currentfileVersionId,
  }) as [Record<string, SidebarModuleEntry>];

  useEffect(() => {
    if (isPageOpenedThroughTransmittal) {
      setInputHeading(`TS-${currentVersionNumber}`);
    } else {
      setInputHeading(`V${currentVersionNumber}`);
    }
  }, [isPageOpenedThroughTransmittal, currentVersionNumber]);

  // The selector already scopes this to the file now open.
  const showConstituentWarning = Object.values(fileAccess?.files ?? {}).some(
    (file) => file.isDeleted || !file.canViewFile,
  );

  const handleVersionDropDownClick = () => {
    if (!(estimateId || scheduleId) || [PDF_ENGINE].includes(engineType)) {
      setOpenVersionDropdown(!openVersionDropdown);
    }
  };

  const getResroucetypeOfCurrentFileVersion = () => {
    if (!fileVersions || !currentfileVersionId) return "";
    for (const fileVersionObjs of Object.values(fileVersions)) {
      if (!Array.isArray(fileVersionObjs)) continue;
      for (const fv of fileVersionObjs) {
        const version = fv as SourceFileVersionData & { resourceType?: string };
        if (version?.id === currentfileVersionId) return version?.resourceType || "";
      }
    }
    return "";
  };

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps & { className?: string }) => (
    <Tooltip
      arrow
      {...props}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -7],
              },
            },
          ],
        },
      }}
      classes={{ popper: className }}
    />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#192048",
      color: "#FFF",
      maxWidth: "28.125rem",
      maxHeight: "9.375rem",
      height: "auto",
      width: "auto",
      borderRadius: "0.25rem",
      border: "none",
      position: "relative",
      display: "flex",
      gap: "1rem",
      padding: "1rem 2rem",
      "& p": {
        color: "#FFF",
        fontFamily: '"Source Sans 3"',
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "normal",
        flexGrow: 1,
        wordBreak: "break-word",
      },
      "& button": {
        color: "#FFF",
        fontFamily: '"Source Sans 3"',
        fontSize: "0.875rem",
        fontStyle: "normal",
        fontWeight: 700,
        lineHeight: "normal",
        whiteSpace: "nowrap",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        "inset-inline-start": 0,
        "inset-inline-end": 0,
        bottom: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
      },
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: "#192048",
      "&::before": {
        backgroundColor: "#192048",
      },
    },
  }));

  const renderProcessingStatusIcon = () => {
    if (!constituentFilesProcessingStatus || constituentFilesProcessingStatus.status === "COMPLETED") return null;

    const IconSrc =
      constituentFilesProcessingStatus.status === "ERROR"
        ? fileConversionStatusUnsuccessfull
        : fileConversionStatusInProgress;
    const handleButtonClick = () => {
      if (constituentFilesProcessingStatus.status === "PROCESSING") {
        globalThis.location.reload();
      }
      if (constituentFilesProcessingStatus.status === "ERROR") {
        dispatch(
          setModalStatus({
            ...HoopsModalStatus,
            ConstituentFiles: _.get(HoopsModalStatus, "ConstituentFiles.show")
              ? { show: false }
              : {
                  show: true,
                  currentStage: "constituentFiles",
                  defaultStage: "constituentFiles",
                  backStage: "constituentFiles",
                },
          }),
        );
      }
    };
    return (
      <HtmlTooltip
        title={
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <p>{constituentFilesProcessingStatus.message}</p>
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleButtonClick}>
              <b>{constituentFilesProcessingStatus.buttonContent}</b>
            </ButtonComponent>
          </div>
        }
      >
        <IconButton
          className="px-2 py-0"
          sx={{
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            },
          }}
        >
          <IconSrc />
        </IconButton>
      </HtmlTooltip>
    );
  };

  const handleFileVersionRedirectClick = (e: React.MouseEvent<HTMLElement>) => {
    const fileVersionId = (e.currentTarget.attributes as NamedNodeMap & Record<string, Attr>)["data-fileversionid"]!
      .value;
    // `spaceId` is a URL query param (`string | null`) and `getViewerUrl` returns `undefined` when
    // `projectId` is falsy; both are consumed unguarded here. Erased assertions only.
    const spaceId1 = spaceId as string;
    const currentMode = new URLSearchParams(globalThis.location.search).get("mode");
    const viewerUrl = getViewerUrl({
      fileVersionId,
      spaceId: spaceId1,
      projectId: projectData._id,
      mode: currentMode === "scs" || currentMode === "skipProperties" ? currentMode : undefined,
      redirectTo: extractRedirectUrl(location),
    }) as string;
    resetDesignViewer();
    navigate(viewerUrl, { replace: true });
    setOpenVersionDropdown(false);
    handleFileVersionIdChange(fileVersionId);
  };

  const handleTransmittalFileVersionRedirectClick = (e: React.MouseEvent<HTMLElement>, url: string) => {
    const fileVersionId = (e.currentTarget.attributes as NamedNodeMap & Record<string, Attr>)["data-fileversionid"]!
      .value;
    const transmittalIdMatch = url.match(/[?&]transmittalId=([^&]*)/);
    const transmittalId = transmittalIdMatch ? decodeURIComponent(transmittalIdMatch[1]!) : null;
    handleFileVersionIdChange(fileVersionId);
    // KNOWN BUG, preserved verbatim: `transmittalId` is `null` when the url carries no
    // transmittalId, yet DesignViewer's setter is a `useState<string>` setter. Erased assertion.
    setTransmittalId(transmittalId!);
    resetDesignViewer();
    navigate(url, { replace: true });
    setOpenVersionDropdown(false);
  };

  const handleFileVersionCompareClick = (val: FileVersionDropDownItem) => {
    setOpenVersionDropdown(false);
    if ([PDF_ENGINE].includes(engineType)) {
      dispatch(
        setViewerState({
          pdfCompare: {
            active: true,
            baseFileVersionId: currentfileVersionId,
            revisionFileVersionId: val.fileVersionId,
          },
        }),
      );
      return;
    }
    handleComparedFileModelChange(val.awsFileName.split(","));
    handleComparedFileVersionChange(val.fileVersionId);
    dispatch(setShowOnlyModal("ModelCompare"));
  };

  const navigateToRedirectOrBack = () => {
    // A recipient's only "away from the viewer" is the share landing/browser. The internal path
    // below would fall back to extractRedirectUrlAsString's /dashboard (share URLs carry no
    // redirectUrl param), and the router would then bounce the guest to sign-in.
    if (isShareView) {
      const session = getShareViewerSession();
      if (session) {
        // QA r4 #1: the viewer route carries the folder the recipient was browsing as ?folder=
        // (set by the shared browser's open). Handing it back re-enters the browser at that folder;
        // a file-only link has no marker and lands on its landing card, unchanged.
        const folderParam = new URLSearchParams(location.search).get("folder");
        navigate(`/share/${session.token}${folderParam ? `?folder=${encodeURIComponent(folderParam)}` : ""}`);
        return;
      }
    }
    const redirectUrl = extractRedirectUrlAsString(location);
    redirectUrl ? navigate(redirectUrl) : navigate(-1);
  };

  const cleanupViewer = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    resetDesignViewer();
  };

  const executeMinimizeActions = () => {
    navigateToRedirectOrBack();
    dispatch(
      setMinimizerData({
        path: location.pathname + location.search,
        text: fileName,
      }),
    );
  };

  const executeCloseActions = () => {
    // Never window.close() for a recipient: a share link usually arrives from mail/chat, where
    // `opener` can be a foreign page — closing the whole tab is not "close the viewer".
    if (!isShareView && globalThis.opener) {
      globalThis.close();
    } else {
      navigateToRedirectOrBack();
    }
  };

  const handleMinimizeViewer = () => {
    cleanupViewer();
    executeMinimizeActions();
  };

  const handleCloseViewer = () => {
    cleanupViewer();
    executeCloseActions();
  };

  const handleFullScreenViewer = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (
      document.documentElement.requestFullscreen &&
      typeof document.documentElement.requestFullscreen === "function"
    ) {
      document.documentElement.requestFullscreen();
    }
  };

  const handleClickFileListDropDown = () => {
    if (!(estimateId || scheduleId)) {
      setFileNameDropdown(!FileNameDropdown);
    }
  };

  const getFiles = async () => {
    if (isPageOpenedThroughTransmittal) {
      // External entry point: both ids are URL query params, and the transmittal path is only
      // reachable with them present. Each is adopted once here, at its declaration (the backend
      // route schema validates them).
      const transmittalId = Url.get("transmittalId") as ObjectIdString;
      const transmittalSpaceId = spaceId as ObjectIdString;
      const allTransmittalFiles = await getTransmittalAttachedFiles({
        transmittalId,
        spaceId: transmittalSpaceId,
      });
      setChildrenFiles(allTransmittalFiles.data as HeaderFileListItem[]);
    } else {
      const shortcutParentId = Url.get("shortcutParentId");
      const finalParentId = shortcutParentId || parentId;
      if (finalParentId) {
        try {
          const allFiles = await getChildrenFilesName({ finalParentId, spaceId: spaceId! });
          setChildrenFiles(
            allFiles.filter(
              (file: HeaderFileListItem) =>
                (
                  ["RESOURCE_TYPE_MERGED_FILE", "RESOURCE_TYPE_SHORTCUT_FILE", "RESOURCE_TYPE_FILE"] as (
                    | string
                    | undefined
                  )[]
                ).includes(file.resourceType) && file.permission?.read,
            ),
          );
        } catch (err) {
          Promise.reject(err);
          setChildrenFiles([]);
        }
      }
    }
  };

  useEffect(() => {
    if (parentId && spaceId) {
      getFiles();
    }
  }, [parentId, spaceId, location.search]);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      !(
        target.closest("#filesListDropDown") ||
        target.closest("#versionsListDropDown") ||
        target.closest("#layoutDropDown")
      )
    ) {
      setFileNameDropdown(false);
      setOpenVersionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleIconsMouseOver = (e: React.MouseEvent<HTMLElement>) => {
    const type = (e.currentTarget.attributes as AttributesByName).id.value;

    if (type == MAXIMIZE) {
      if (document.fullscreenElement) {
        setMaximizeIconState(ICON_ARROW_MINIMIZE);
      } else {
        setMaximizeIconState(ICON_ARROW_MAXIMIZE);
      }
    } else if (type == MINIMIZE) {
      setShowMinimizeIcon(true);
    } else if (type == DISMISS) {
      setShowDismissIcon(true);
    }
  };

  useEffect(() => {
    if (PDF_ENGINE.includes(engineType)) {
      const modalStatus: Record<string, ModalEntry> = {};
      dispatch(setViewerState({ sidebarData: ["Pages", "Issues", "Properties"] }));
      if (modalToOpen.length > 0) {
        modalToOpen = modalToOpen.filter((data: string) => modulesMapper[data]?.isVisible);
        for (const dock of modalToOpen) {
          const initializeState = modulesMapper[dock]?.initialState || {};
          modalStatus[dock] = {
            show: true,
            ...initializeState,
          };

          if (dock === "Issues") {
            modalStatus[dock]!.currentStage = "view_issue";
          }
        }
      }
      dispatch(setModalStatus(modalStatus));
    }
  }, [engineType]);

  const handleIconsMouseOut = (e: React.MouseEvent<HTMLElement>) => {
    const type = (e.currentTarget.attributes as AttributesByName).id.value;

    if (type == MAXIMIZE) {
      setMaximizeIconState(ICON_DEFAULT);
    } else if (type == MINIMIZE) {
      setShowMinimizeIcon(false);
    } else if (type == DISMISS) {
      setShowDismissIcon(false);
    }
  };

  const handleHelpSectionClick = () => {
    dispatch(setHelpSectionState({ display: !helpSectionState.display, module: "general" }));
  };

  const handleChangeTransmittalFile = (transmittalFileObj: HeaderFileListItem) => {
    // Same two unguarded values as `handleFileVersionRedirectClick`: a `string | null` query param
    // and `getViewerUrl`'s `string | undefined`. Erased assertions only.
    const transmittalVersionId = Url.get("transmittalId") as string;
    const transmittalFileLink = getViewerUrl({
      fileVersionId: transmittalFileObj.fileVersionId,
      spaceId: transmittalFileObj.toSpace,
      transmittalVersionId,
      projectId: projectData.id,
      redirectTo: extractRedirectUrl(location),
    }) as string;
    resetDesignViewer();
    navigate(transmittalFileLink, { replace: true });
    setFileNameDropdown(false);
  };

  const handleChangeModel = (fileVersionId: string | undefined) => {
    if (currentfileVersionId != fileVersionId) {
      const spaceId1 = spaceId as string;
      const viewerUrl = getViewerUrl({
        fileVersionId,
        spaceId: spaceId1,
        projectId: projectData._id,
        redirectTo: extractRedirectUrl(location),
      });
      resetDesignViewer();
      navigate(viewerUrl as string, { replace: true });
      setFileNameDropdown(false);
    }
  };

  return (
    <div className="z-20" data-testid="designViewerHeader">
      <div
        className="flex items-center rounded-lg bg-[linear-gradient(90.03deg,rgba(255,255,255,0.8)_0%,rgba(255,255,255,0.4)_100%)] py-2 ps-6 text-[#333]"
        data-testid="fileInfoSection"
      >
        <div className="w-14">
          {isShareView ? (
            <>
              {/* QA r4 #2: the burger opens the window controls for recipients. Close/Minimize
                  route through the share-aware handlers (folder-links return to the folder,
                  file-only links to their landing — QA r4 #1). */}
              <button
                type="button"
                onClick={(event) => setShareBurgerAnchor(event.currentTarget)}
                aria-label={t("Menu")}
                data-testid="shareBurgerMenu"
                className="grid cursor-pointer place-items-center rounded-sm p-1 hover:bg-alice_blue"
              >
                <GiHamburgerMenu size="1.2rem" color="#555555" />
              </button>
              <MenuComponent
                anchorEl={shareBurgerAnchor}
                open={!!shareBurgerAnchor}
                onClose={() => setShareBurgerAnchor(null)}
              >
                <MenuItemComponent
                  onClick={() => {
                    setShareBurgerAnchor(null);
                    handleMinimizeViewer();
                  }}
                >
                  {t("Minimize")}
                </MenuItemComponent>
                <MenuItemComponent
                  onClick={() => {
                    setShareBurgerAnchor(null);
                    handleFullScreenViewer();
                  }}
                >
                  {t("Maximise")}
                </MenuItemComponent>
                <MenuItemComponent
                  onClick={() => {
                    setShareBurgerAnchor(null);
                    handleCloseViewer();
                  }}
                >
                  {t("Close")}
                </MenuItemComponent>
              </MenuComponent>
            </>
          ) : (
            <GiHamburgerMenu size="1.2rem" color="#555555" />
          )}
        </div>
        <div className="grid grid-flow-col auto-cols-max items-center" data-testid="leftInfoSection">
          {isShareView ? (
            <div className="flex items-center border-e border-black pe-2" id="filesListDropDown">
              <ToolTipComponent position="right" content={fileName as TranslatedString}>
                <div data-testid="fileName" className="max-w-72 truncate text-base font-semibold">
                  {fileName}
                </div>
              </ToolTipComponent>
              {showConstituentWarning && (
                <ToolTipComponent position="bottom" content={t("Some files in this model are missing or restricted.")}>
                  <span className="flex items-center px-1" data-testid="constituentWarningIcon">
                    <RiErrorWarningFill className="text-red-500" size={18} />
                  </span>
                </ToolTipComponent>
              )}
              {renderProcessingStatusIcon()}
              {/* QA r4 #2: siblings from the folder the recipient was browsing, in the STANDARD
                  menu. No siblings (a file-only link) → no caret at all, never "No files to Show". */}
              {shareSiblings.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={(event) => setShareFilesAnchor(event.currentTarget)}
                    aria-label={t("Files")}
                    data-testid="shareFileDropdown"
                    className="flex cursor-pointer items-center"
                  >
                    <MdArrowDropDown size="1.5rem" />
                  </button>
                  <MenuComponent
                    anchorEl={shareFilesAnchor}
                    open={!!shareFilesAnchor}
                    onClose={() => setShareFilesAnchor(null)}
                    width="18rem"
                  >
                    {shareSiblings.map((sibling) => (
                      <MenuItemComponent
                        key={sibling.versionId}
                        disabled={sibling.versionId === currentfileVersionId}
                        onClick={() => {
                          setShareFilesAnchor(null);
                          if (sibling.versionId !== currentfileVersionId) navigateToSharedVersion(sibling.versionId);
                        }}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          {getFileIcon(sibling.extension ?? sibling.name)}
                          <span className="truncate">{untranslated(sibling.name)}</span>
                        </span>
                      </MenuItemComponent>
                    ))}
                  </MenuComponent>
                </>
              )}
            </div>
          ) : (
            <Box
              data-testid="fileListDropdown"
              onClick={handleClickFileListDropDown}
              className={`flex pe-2  border-e border-black ${
                estimateId || scheduleId ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
              id="filesListDropDown"
            >
              <ToolTipComponent
                position="right"
                // File and folder names are user data, not locale keys, so they are asserted here
                // rather than translated.
                content={fileName as TranslatedString}
              >
                <div data-testid="fileName" className="max-w-72  truncate text-base font-semibold">
                  {showDocAndRevisionInViewer ? documentNumber || fileName : fileName}
                </div>
              </ToolTipComponent>
              {showConstituentWarning && (
                <ToolTipComponent position="bottom" content={t("Some files in this model are missing or restricted.")}>
                  <span className="flex items-center px-1" data-testid="constituentWarningIcon">
                    <RiErrorWarningFill className="text-red-500" size={18} />
                  </span>
                </ToolTipComponent>
              )}
              {renderProcessingStatusIcon()}
              <MdArrowDropDown size="1.5rem" />
            </Box>
          )}

          {showDocAndRevisionInViewer && (
            <Box className="flex px-2 border-e border-black" data-testid="viewerRevision">
              <ToolTipComponent position="bottom" content={t("Revision")}>
                <div className="truncate text-base font-semibold">{revisionLabel || "-"}</div>
              </ToolTipComponent>
            </Box>
          )}

          {/* Recipient version switcher (FR-18/FR-40: the latest version is the DEFAULT selection,
              not a restriction). Fed from the share session's prefetched list — getSharedFile returns
              the full history — and switching navigates between /share viewer routes, so no
              authenticated call is involved. */}
          {isShareView && shareVersions.length > 0 && (
            <div className="flex items-center border-e border-black pe-2 ps-3">
              <button
                type="button"
                onClick={(event) => setShareVersionAnchor(event.currentTarget)}
                className="flex cursor-pointer items-center gap-1 text-base font-semibold"
                data-testid="shareVersionDropdown"
              >
                {untranslated(shareVersions.find((v) => v.versionId === currentfileVersionId)?.label ?? "")}
                <MdArrowDropDown size="1.5rem" />
              </button>
              <MenuComponent
                anchorEl={shareVersionAnchor}
                open={!!shareVersionAnchor}
                onClose={() => setShareVersionAnchor(null)}
                width="13rem"
              >
                {shareVersions.map((version) => (
                  <MenuItemComponent
                    key={version.versionId}
                    disabled={version.versionId === currentfileVersionId}
                    onClick={() => {
                      setShareVersionAnchor(null);
                      if (version.versionId !== currentfileVersionId) navigateToSharedVersion(version.versionId);
                    }}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{untranslated(version.label)}</span>
                      {typeof version.createdAt === "number" && (
                        <span className="text-xs text-text_light">
                          {untranslated(convertDate(version.createdAt, "dateForText"))}
                        </span>
                      )}
                    </span>
                  </MenuItemComponent>
                ))}
              </MenuComponent>
            </div>
          )}
          {!isShareView && (
            <Box
              data-testid="fileVersionDropdown"
              onClick={handleVersionDropDownClick}
              className={`flex pe-2 ps-3 border-e border-black ${
                (estimateId || scheduleId) && ![PDF_ENGINE].includes(engineType)
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              id="versionsListDropDown"
            >
              <div data-testid="inputHeading" className="text-base font-semibold">
                {inputHeading}
              </div>
              <MdArrowDropDown size="1.5rem" />
              {openVersionDropdown && (
                <div
                  data-testid="fileVersionPopOver"
                  className="absolute overflow-auto -ms-3 border border-white bg-white rounded-lg shadow-md"
                  style={{ top: "4.25rem", maxHeight: "34.375rem" }}
                >
                  <div style={{ height: "100%", overflow: "auto" }} data-testid="fileVersionDropdownItems">
                    {fileVersionsDropDownItems?.map((val: FileVersionDropDownItem) => {
                      const formattedTime = convertDate(Number(val.createdDate), "dateForText");
                      const formattedTimeForToolTip = convertDate(Number(val.createdDate), "dateForTooltip");
                      let isDisabled = false;
                      if (currentVersionNumber == val.versionNumber) {
                        isDisabled = true;
                      }
                      return (
                        <div
                          key={`dropDownVersion_${val.text}`}
                          className="grid grid-cols-[auto_auto] gap-x-4 cursor-pointer px-4 py-2.5"
                        >
                          <Box
                            data-fileversionid={val.fileVersionId}
                            onClick={(e) =>
                              isPageOpenedThroughTransmittal
                                ? handleTransmittalFileVersionRedirectClick(e, val.url)
                                : handleFileVersionRedirectClick(e)
                            }
                            className={`grid w-32 grid-cols-[auto_auto] text-sm font-normal ${isDisabled || estimateId || scheduleId ? "pointer-events-none opacity-50" : ""}`}
                          >
                            <span>{val.text}</span>
                            <span className="ps-4 text-[#979797]">
                              <ToolTipComponent showTipPointer position="bottom" content={formattedTimeForToolTip}>
                                {formattedTime}
                              </ToolTipComponent>
                            </span>
                          </Box>
                          {(([BIM_ENGINE, CAD_ENGINE].includes(engineType) && isDisabled === false) ||
                            [PDF_ENGINE].includes(engineType)) &&
                          getResroucetypeOfCurrentFileVersion() != "RESOURCE_TYPE_MERGED_FILE" ? (
                            <ToolTipComponent position="left" content={t("Compare")}>
                              <Box
                                className="flex cursor-pointer items-center text-accentBlue"
                                onClick={() => handleFileVersionCompareClick(val)}
                              >
                                <BiGitCompare />
                              </Box>
                            </ToolTipComponent>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Box>
          )}
          {/* Hidden for a recipient: FR-19 allows mouse-driven navigation only, no on-screen control
              buttons, and the layout manager is exactly those (panels, measure, markup, cutting). */}
          {!isShareView &&
            [BIM_ENGINE, CAD_ENGINE].includes(engineType) &&
            enableDesignViewerLayoutManager &&
            layout.DESIGNVIEWER && (
              <DesignViewerLayoutManager
                // Only rendered once the shell has reported its rc-dock handle up; erased assertion.
                HoopsViewer={HoopsViewer!}
                modalToOpen={modalToOpen}
                fileVersionId={currentfileVersionId}
              />
            )}
        </div>
        {/* Internal window chrome — help centre, minimise/maximise into the app shell, dismiss back to
            the file manager. None of it applies to a recipient, who has no app shell to return to. */}
        <div className="ms-auto flex items-center gap-4 pe-4" data-testid="rightInfoSection">
          {!isShareView && (
            <>
              <IconComponent
                onClick={handleHelpSectionClick}
                position={"left"}
                toolTipContent={t("Help Section")}
                showGradient={(helpSectionState.display && helpSectionState.module == "general") ?? undefined}
                name="help"
                width="1.5rem"
                height="1.5rem"
              />
              <Box
                onMouseEnter={handleIconsMouseOver}
                onMouseLeave={handleIconsMouseOut}
                className="h-4 w-4 cursor-pointer bg-alice_blue hover:bg-bright_yellow rounded-full flex items-center justify-center transition-colors duration-100"
                id={MINIMIZE}
              >
                <IconComponent
                  key={MINIMIZE}
                  name={"minimizeIcon"}
                  width="0.5rem"
                  height="0.5rem"
                  toolTipContent={t("Minimize")}
                  position="top"
                  onClick={handleMinimizeViewer}
                  data-testid="minimise"
                  parentContainerClass="h-2.5 w-2.5"
                  isVisible={showMinimizeIcon}
                />
              </Box>
              <Box
                onMouseEnter={handleIconsMouseOver}
                onMouseLeave={handleIconsMouseOut}
                className="h-4 w-4 cursor-pointer bg-alice_blue hover:bg-green-600 rounded-full flex items-center justify-center transition-colors duration-100"
                id={MAXIMIZE}
              >
                <IconComponent
                  key={MAXIMIZE}
                  name={maximizeIconState == ICON_ARROW_MINIMIZE ? "exitFullScreen" : "enterFullScreen"}
                  toolTipContent={maximizeIconState == ICON_ARROW_MINIMIZE ? t("Exit Full Screen") : t("Maximise")}
                  position="top"
                  onClick={handleFullScreenViewer}
                  data-testid="maximise"
                  parentContainerClass="h-3 w-3"
                  isVisible={maximizeIconState !== ICON_DEFAULT}
                  width="1.5rem"
                  height="1.5rem"
                />
              </Box>
              <Box
                onMouseEnter={handleIconsMouseOver}
                onMouseLeave={handleIconsMouseOut}
                className="h-4 w-4 cursor-pointer bg-alice_blue hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-100"
                id={DISMISS}
              >
                <IconComponent
                  key={DISMISS}
                  name={"Cancel"}
                  width="0.75rem"
                  height="1rem"
                  toolTipContent={t("Dismiss")}
                  position="top"
                  onClick={handleCloseViewer}
                  data-testid="dismiss"
                  parentContainerClass="h-4 w-3"
                  isVisible={showDismissIcon}
                />
              </Box>
            </>
          )}
        </div>
      </div>
      {FileNameDropdown && (
        <div
          className="absolute overflow-auto border border-white bg-white rounded-lg shadow-md"
          style={{ top: "4.25rem", maxHeight: "75%", insetInlineStart: "6.5rem" }}
          data-testid="filesListData"
        >
          <div className="h-full overflow-auto w-72" data-testid="filesListDataContent">
            {childrenFiles.length ? (
              childrenFiles.map((val: HeaderFileListItem) => (
                <ToolTipComponent key={val.id} position="right" content={val.name as TranslatedString}>
                  <Box
                    onClick={() =>
                      isPageOpenedThroughTransmittal
                        ? handleChangeTransmittalFile(val)
                        : handleChangeModel(val.fileVersion)
                    }
                    className={`flex max-w-72 whitespace-nowrap items-center p-3 gap-2 ${currentfileVersionId == val.fileVersion ? "cursor-not-allowed" : "cursor-pointer"} hover:bg-primary_blue`}
                  >
                    <div>
                      <img src={getFileImage(val) as string} className="max-w-none" alt="file-icon" />
                    </div>
                    <div key={val.id} className="truncate " data-testid="filesListName">
                      {val.name}
                    </div>
                  </Box>
                </ToolTipComponent>
              ))
            ) : (
              <div
                className="grid h-[3vh] w-[25vw] items-center ps-1 italic text-[#979797]"
                data-testid="noFilesToShow"
              >
                <Text>{t("No files to Show")}</Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DesignViewerHeader;
